import { useEffect, useState } from "react";

/** Alpha below this is treated as fully transparent. */
const ALPHA_THRESHOLD = 128;

/** Min fraction of transparent pixels required to call a logo "on transparent". */
const TRANSPARENT_RATIO_MIN = 0.2;

/** Avg brightness (0-255) of opaque pixels above which the foreground is treated as near-white. */
const LIGHT_FG_AVG_MIN = 220;

/** Pixel brightness above which an opaque pixel is considered a white mark. */
const LIGHT_PIXEL_MIN = 245;

/** Min fraction of opaque pixels that must be white for mixed-color logos. */
const LIGHT_PIXEL_RATIO_MIN = 0.25;

/**
 * @typedef {"default" | "light-on-transparent"} LogoAppearance
 */

/** @type {Map<string, Promise<LogoAppearance>>} */
const detectCache = new Map();

/**
 * Decode the image and classify its surface for picking a container treatment.
 * Resolves "default" on decode/canvas failure (e.g. CORS-tainted remote URL).
 * @param {string} src
 * @returns {Promise<LogoAppearance>}
 */
function detectLogoAppearance(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onerror = () => resolve("default");
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) {
          resolve("default");
          return;
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve("default");
          return;
        }
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, w, h).data;
        const total = w * h;

        let opaqueCount = 0;
        let transparentCount = 0;
        let lightPixelCount = 0;
        let brightnessSum = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < ALPHA_THRESHOLD) {
            transparentCount++;
            continue;
          }
          opaqueCount++;
          const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
          brightnessSum += brightness;
          if (brightness >= LIGHT_PIXEL_MIN) {
            lightPixelCount++;
          }
        }
        if (opaqueCount === 0) {
          resolve("default");
          return;
        }
        const transparentRatio = transparentCount / total;
        const avgBrightness = brightnessSum / opaqueCount;
        const lightPixelRatio = lightPixelCount / opaqueCount;
        // Mixed-color marks (e.g. white arch + dark wordmark) can drag the
        // overall average below `LIGHT_FG_AVG_MIN`; treat them as light when a
        // meaningful share of opaque pixels are near-white.
        const isLight =
          transparentRatio >= TRANSPARENT_RATIO_MIN
          && (avgBrightness >= LIGHT_FG_AVG_MIN
            || lightPixelRatio >= LIGHT_PIXEL_RATIO_MIN);
        resolve(isLight ? "light-on-transparent" : "default");
      } catch {
        resolve("default");
      }
    };
    img.src = src;
  });
}

/**
 * @param {string} src
 * @returns {Promise<LogoAppearance>}
 */
function getLogoAppearance(src) {
  const hit = detectCache.get(src);
  if (hit) return hit;
  const p = detectLogoAppearance(src);
  detectCache.set(src, p);
  return p;
}

/**
 * Returns the classification used to pick a container treatment for a tenant logo.
 * Resolves to "default" while detection runs and on failure.
 * @param {string|null|undefined} src
 * @returns {LogoAppearance}
 */
export function useLogoAppearance(src) {
  const [appearance, setAppearance] = useState("default");

  useEffect(() => {
    setAppearance("default");
    if (!src) return;
    let cancelled = false;
    getLogoAppearance(src).then((result) => {
      if (!cancelled) setAppearance(result);
    });
    return () => {
      cancelled = true;
    };
  }, [src]);

  return appearance;
}
